import {
  Box,
  Button,
  Container,
  FilledInput,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useState } from 'react';
import { getContactosByUser } from '../../../services/Common/Contacto';
import { ArrowBackRounded, ArrowForwardRounded, PersonRounded } from '@mui/icons-material';
import Turnstile from '../../Common/Turnstile';
import { LoadingButton } from '@mui/lab';
import SinCorreo from './SinCorreo';
import SeleccionarContacto from './SeleccionarContacto';
import MensajeDeConfirmacionModal from '../../Notifications/MensajeDeConfirmacionModal';
import CustomizedSnackbars from '../../Notifications/SnackBar';
import ButtonBack from '../../Breadcrumb/ButtonBack';

/**
 * Pantalla de recuperación de contraseña/PIN.
 * Permite al usuario ingresar su CUIL o N° de Afiliado, validar Turnstile,
 * y recibir instrucciones de recuperación por correo electrónico.
 */
export const OlvidePinMobile = () => {
  // Estado del flujo
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [responseSeverity, setResponseSeverity] = useState('error');
  const [turnstileValidado, setTurnstileValidado] = useState(false);

  // Estado de los modales/dialogs
  const [contactos, setContactos] = useState(null);
  const [sinContacto, setSinContacto] = useState(false);
  const [openSeleccionar, setOpenSeleccionar] = useState(false);
  const [openMensajeConfirmacion, setOpenMensajeConfirmacion] = useState(false);

  const history = useHistory();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onSubmit' });

  // watch es reactivo: causa re-render al cambiar el valor del campo
  const watchedUsername = watch('username', '');

  // --- Handler del formulario ---
  const recuperarPinHandler = async (data) => {
    setLoading(true);
    setResponse('');

    try {
      const res = await getContactosByUser(data.username);

      if (res?.length > 0) {
        setOpenSeleccionar(true);
        setContactos(res);
      } else {
        setSinContacto(true);
      }
    } catch (err) {
      setResponseSeverity('error');
      setResponse(
        'Ocurrió un error al intentar recuperar su contraseña. Por favor, intente nuevamente más tarde.',
      );
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers de modales ---
  const handleCloseSinContacto = () => {
    setSinContacto(false);
    setLoading(false);
  };

  const handleCloseSeleccionar = (contacto) => {
    setOpenSeleccionar(false);
    setLoading(false);
    setOpenMensajeConfirmacion(!!contacto);
  };

  const handleCloseMensajeConfirmacion = () => {
    setOpenMensajeConfirmacion(false);
    history.push('/signin');
  };

  return (
    <Container component='main' maxWidth='sm'>
      <Box sx={{ height: '32px', display: 'flex', mb: 3 }}>
        <ButtonBack title={'Mi OSER'} />
      </Box>

      <Typography variant='h5' component='h1' gutterBottom>
        Recuperar Contraseña/PIN
      </Typography>

      <Typography variant='body1' gutterBottom mb={4}>
        Ingresá tu CUIL o Número de Afiliado para recibir las instrucciones de recuperación.
      </Typography>

      <form noValidate onSubmit={handleSubmit(recuperarPinHandler)} style={{ width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              background: '#fff',
              padding: '32px 24px',
              borderRadius: '8px',
            }}
          > */}
            {/* Campo CUIL / N° Afiliado */}
            <FormControl fullWidth error={!!errors.username} variant='filled'>
              <InputLabel htmlFor='username'>CUIL o Nº Afiliado</InputLabel>
              <FilledInput
                sx={{
                  backgroundColor: '#fff',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.08)',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#ffffff',
                  },
                }}
                fullWidth
                autoComplete='username'
                startAdornment={
                  <InputAdornment position='start'>
                    <PersonRounded />
                  </InputAdornment>
                }
                inputProps={{
                  inputMode: 'numeric',
                }}
                {...register('username', {
                  required: {
                    value: true,
                    message: 'Debe ingresar CUIL o N° Afiliado',
                  },
                })}
              />
              {errors.username && <FormHelperText error>{errors.username.message}</FormHelperText>}
            </FormControl>
          {/* </Box> */}

          {/* Turnstile */}
          <FormControl fullWidth>
            <Box
              sx={{
                my: 1,
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                background: '#fff',
                padding: '24px 24px',
                borderRadius: '8px',
              }}
            >
              <Turnstile
                sitekey={process.env.REACT_APP_IOSPER_TURNSTILE_SITE_KEY}
                onTurnstileValidado={(validado) => {
                  setLoading(false);
                  setTurnstileValidado(validado);
                }}
                onChange={() => setLoading(true)}
                onError={() => setLoading(true)}
              />
            </Box>
          </FormControl>

          {/* Botón submit */}
          <FormControl fullWidth>
            <LoadingButton
              variant='contained'
              color='primary'
              type='submit'
              loading={loading}
              endIcon={<ArrowForwardRounded />}
              disabled={!turnstileValidado || !watchedUsername}
            >
              Recuperar Contraseña
            </LoadingButton>
          </FormControl>

          {/* Botón volver */}
          <FormControl fullWidth>
            <Button
              variant='text'
              color='primary'
              onClick={() => history.push('/signin')}
              startIcon={<ArrowBackRounded />}
            >
              Volver al Inicio de Sesión
            </Button>
          </FormControl>
        </Box>
      </form>

      {/* --- Modales --- */}
      <SinCorreo open={sinContacto} onClose={handleCloseSinContacto} />

      <SeleccionarContacto
        contactos={contactos}
        username={watchedUsername}
        open={openSeleccionar}
        onClose={handleCloseSeleccionar}
      />

      <MensajeDeConfirmacionModal
        titleModal='Envío confirmado'
        messagePrimary='Se envió un correo a su casilla para completar la recuperación de su contraseña.'
        messageSecundary='¿No recibió ningún correo? Recuerde revisar en el correo no deseado.'
        open={openMensajeConfirmacion}
        onClose={handleCloseMensajeConfirmacion}
      />

      {/* Notificación de error/respuesta */}
      <CustomizedSnackbars
        open={response}
        severity={responseSeverity}
        message={response}
        onDeleteMessage={() => setResponse('')}
      />
    </Container>
  );
};
